<script lang="ts">
  import { titleGlyphs } from '../lib/event-display';
  import { config, getDisplayByFeed, pushLog, selection, clearSelection, moveEventsToLane, copyEventsToLane, deleteLocalEvents, focus, ui, effectiveFeedTz, isKiosk, markerRange, markerIsSpan, undoLastChange, stepFontSize, canResizeUi, fontSizeLabel } from '../lib/state.svelte';
  import { uiSizeGesture } from '../lib/ui-size-gesture';
  import { online } from '../lib/online.svelte';
  import { swStatus } from '../lib/sw-status.svelte';
  import { undoBar, dismissUndoBar } from '../lib/undo.svelte';
  import { viewport } from '../lib/viewport.svelte';
  import { today, dayStartMs } from '../lib/today.svelte';
  import { clock } from '../lib/clock.svelte';
  import { startOfDay, addDays, addMonths, isoWeekNumber, intersectDaySpan, MS_PER_DAY } from '../lib/time';
  import { formatDate, formatDateLong, formatDayCount, formatMonth, formatSpanLabel, formatTime, formatNextRelative, durationDays, zonedDateProxy } from '../lib/format';
  import Icon from './Icon.svelte';
  import ConfirmButton from './ConfirmButton.svelte';
  import CalendarDownloadMenu from './CalendarDownloadMenu.svelte';
  import CopyIconButton from './CopyIconButton.svelte';
  import { trayExpand, trayCollapse } from '../lib/haptics';
  import type { DisplayEvent, FeedCategory, ParsedEvent } from '../lib/types';
  import { untrack } from 'svelte';
  import { pickStatusEvent, formatTimeLeft, type StatusEvent } from '../lib/next-event';

  // The collapsed tray height tracks the header's rendered height — it now carries
  // vertical padding (to match the bottom toolbar) and scales with the font-size
  // setting, so a fixed value would let the header spill below the screen. Measured
  // from the live `.handle` via bind:clientHeight; 22 is the pre-measure fallback.
  let collapsedHeight = $state(22);
  const MAX_HEIGHT_VH = 60;
  // The chip flashes the version for a few seconds — at startup and again on each
  // tap that opens What's new — then settles back to ONLINE/OFFLINE.
  const VERSION_FLASH_MS = 3000;
  let showVersion = $state(true);
  let versionTimer: ReturnType<typeof setTimeout> | undefined;
  function flashVersion(): void {
    showVersion = true;
    clearTimeout(versionTimer);
    versionTimer = setTimeout(() => { showVersion = false; }, VERSION_FLASH_MS);
  }
  $effect(() => {
    if (typeof window === 'undefined') return;
    flashVersion();
    return () => clearTimeout(versionTimer);
  });

  let dragging = $state(false);
  let dragStartY = 0;
  let dragStartHeight = 0;
  // A pointer that ends within this many px of where it started is a tap, not a
  // drag — so a click (which can jitter a few px) reliably toggles the tray open
  // instead of being misread as a tiny drag and snapped shut.
  const TAP_SLOP_PX = 10;
  let height = $state(28);
  // 0 until a real expanded height is recorded (drag/expand), so the FIRST
  // header tap falls through to maxHeight() and fully opens (28 — the initial
  // seed — is just above the collapsed threshold, so it opened only a sliver).
  let lastExpandedHeight = 0;
  // Swipe-down-to-dismiss on the tray body (not the header). Armed on pointerdown
  // only when the inner scroll region is at the top; once the finger has moved a
  // small distance we lock its axis and either hand a downward pull to the same
  // live drag the header runs, or release a sideways/upward swipe to native
  // scrolling. Small enough that the drag feels immediate, like the header.
  const AXIS_LOCK_PX = 8;
  let trayArmed = false;
  let trayArmStartY = 0;
  let trayArmStartX = 0;
  let trayArmTarget: HTMLElement | null = null;
  let trayArmPointerId = 0;

  const expanded = $derived(height > collapsedHeight + 2);
  // Resting collapsed height: a touch taller than the header so the bar sits
  // comfortably at the screen bottom without clipping its content.
  const closedHeight = $derived(collapsedHeight + 2);

  // 'auto' follows the device (left on desktop, bottom on mobile); 'left'/'bottom'
  // force a side. When left mode is on the tray becomes a side panel that slides
  // in beside the timeline instead of growing up over it.
  const leftMode = $derived(
    config.traySide === 'left' ? true : config.traySide === 'bottom' ? false : viewport.isDesktop,
  );
  // The one "is the tray open" flag. In bottom mode it tracks the dragged height;
  // in left mode the height never grows, so it tracks the explicit expand flag.
  const trayOpen = $derived(leftMode ? ui.statusExpanded : expanded);

  // Tapping the chip — version or ONLINE/OFFLINE — opens What's new instead of
  // toggling the tray, and flashes the version. It lives inside the handle button (no nested buttons), so the tap is routed
  // by where the press began — pointer capture retargets the release. The dialog
  // opens on the click, never on pointerup: on touch the click that follows a tap
  // would land on the freshly mounted backdrop and close it in the same frame.
  // The next-event label is routed the same way: tapping it opens that event's
  // card (and scrolls to it) rather than toggling the tray.
  type PressTarget = 'version' | 'next' | null;
  let pressOn: PressTarget = null;

  function pressedTarget(e: Event): PressTarget {
    if (isKiosk() || !(e.target instanceof Element)) return null;
    if (e.target.closest('.status-chip')) return 'version';
    if (nextEvent && e.target.closest('.next-event')) return 'next';
    return null;
  }

  function onHandleClick(e: MouseEvent): void {
    // Left mode skips startDrag's capture, so the click's own target is enough.
    const target = leftMode ? pressedTarget(e) : pressOn;
    pressOn = null;
    if (target === 'version') {
      ui.whatsNewOpen = true;
      flashVersion();
    } else if (target === 'next' && nextEvent) {
      ui.modalEvent = nextEvent;
      window.dispatchEvent(
        new CustomEvent('cal:scroll-to-date', { detail: { date: nextEvent.start, utcDay: nextEvent.allDay } }),
      );
    }
    else if (leftMode) toggleExpand();
  }

  // One chevron (drawn pointing up), rotated to point the way the tray edge
  // travels on click: bottom up/down (0/180), left right/left (90/270).
  const toggleDeg = $derived(leftMode ? (trayOpen ? 270 : 90) : (trayOpen ? 180 : 0));

  // Keep the collapsed bar at the resting height whenever it isn't expanded
  // (covers the initial measure, font-size changes, and the header shrinking
  // back when selection mode exits). In left mode the bar never grows, so it
  // stays pinned to the resting height regardless of the expand flag.
  $effect(() => {
    if (leftMode || !ui.statusExpanded) height = closedHeight;
  });
  const inSelectionMode = $derived(selection.mode && selection.uids.size > 0);
  // The side panel is present (mounted) whenever the tray would show content.
  const sidePanelShown = $derived(trayOpen || inSelectionMode);
  // Push the timeline right by the panel width only while the left panel is open.
  $effect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (leftMode && sidePanelShown) root.style.setProperty('--tray-left-w', 'var(--tray-side-w)');
    else root.style.setProperty('--tray-left-w', '0px');
    return () => root.style.setProperty('--tray-left-w', '0px');
  });
  // Publish the resting bottom-bar height so fixed-height views (the 1W grid)
  // reserve exactly the space the fixed bar occupies. Tying the reservation to
  // the same measured number that sizes the bar keeps the two from drifting into
  // a paper sliver (or an overlap) at the grid's bottom edge — the bar is always
  // this tall at the bottom, in both bottom and left-panel modes.
  $effect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.style.setProperty('--tray-bottom-h', `${closedHeight}px`);
    return () => root.style.removeProperty('--tray-bottom-h');
  });

  // Local lanes (Draft + imported .ics) — destinations for move/copy.
  const localLanes = $derived(config.feeds.filter((f) => f.source.kind === 'scratchpad'));
  // Selected uids that live in a local lane (only those can be deleted/moved).
  const selectedLocalUids = $derived.by(() => {
    const out: string[] = [];
    if (selection.uids.size === 0) return out;
    const byFeed = getDisplayByFeed();
    for (const f of localLanes) {
      for (const ev of byFeed[f.id] ?? []) {
        if (selection.uids.has(ev.uid)) out.push(ev.uid);
      }
    }
    return out;
  });
  const hasLocalSelection = $derived(selectedLocalUids.length > 0);
  // URL-only selection: nothing to delete/move, so the action becomes a copy.
  const copyMode = $derived(selection.uids.size > 0 && !hasLocalSelection);
  // Count shows just the total when the selection is all one kind (all local or
  // all URL); a mixed selection shows "local / total" so it's clear how many can
  // be moved/deleted.
  const selTotal = $derived(selection.uids.size);
  const mixedSelection = $derived(selectedLocalUids.length > 0 && selectedLocalUids.length < selTotal);

  // The undo bar has no timer: it stays until the user does something else.
  // That is a tap outside it (a pan or a drag moves past the slop, so scrolling
  // and dragging the next event don't count — a drag pushes its own entry), or
  // a key other than Ctrl/⌘+Z, an Alt+arrow nudge, or a bare modifier.
  const UNDO_TAP_SLOP_PX = 6;
  let undoBarEl: HTMLElement | undefined = $state();
  $effect(() => {
    if (!undoBar.message || typeof document === 'undefined') return;
    let downX = 0;
    let downY = 0;
    let downOutside = false;
    const onDown = (e: PointerEvent): void => {
      downX = e.clientX;
      downY = e.clientY;
      downOutside = !undoBarEl?.contains(e.target as Node);
    };
    const onUp = (e: PointerEvent): void => {
      if (!downOutside) return;
      if (Math.hypot(e.clientX - downX, e.clientY - downY) > UNDO_TAP_SLOP_PX) return;
      dismissUndoBar();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (['Shift', 'Control', 'Alt', 'Meta'].includes(e.key)) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && (e.key.toLowerCase() === 'z' || e.code === 'KeyZ')) return;
      if (e.altKey && e.key.startsWith('Arrow')) return;
      dismissUndoBar();
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('pointerup', onUp, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('pointerup', onUp, true);
      document.removeEventListener('keydown', onKey, true);
    };
  });

  // --- DELETE / CANCEL use the shared ConfirmButton (tap → ? → ✓ → UNDO n). ---
  // MOVE/COPY mirror its post-confirm timing: ✓ holds for MOVE_DONE_HOLD_MS,
  // then a live "UNDO n" countdown ticks down before the action settles.
  const MOVE_DONE_HOLD_MS = 1000; // ✓ visible before the undo countdown opens
  const MOVE_UNDO_SECONDS = 3; // "UNDO 3 → 2 → 1" countdown before settling

  function commitDelete(): void {
    const removed = [...selectedLocalUids];
    deleteLocalEvents(removed);
    const next = new Set(selection.uids);
    for (const uid of removed) next.delete(uid);
    if (next.size === 0) clearSelection();
    else selection.uids = next;
  }
  function commitCancel(): void {
    focus.feedId = null;
    focus.eventIndex = -1;
    clearSelection();
  }

  // Reset MOVE state whenever we leave selection mode (the DELETE/CANCEL
  // ConfirmButtons unmount with the toolbar, so they self-reset).
  $effect(() => {
    if (!inSelectionMode) {
      clearMoveTimers();
      moveStage = 'idle';
      moveUndo = null;
      moveMenuOpen = false;
    }
  });

  // --- MOVE / COPY submenu (lane pick → ✓ with a cooldown-to-undo "UNDO n"). ---
  let moveMenuOpen = $state(false);
  let moveRoot: HTMLDivElement | undefined = $state();
  let moveStage = $state<'idle' | 'done' | 'undo'>('idle');
  let moveTimer: ReturnType<typeof setTimeout> | null = null;
  let moveInterval: ReturnType<typeof setInterval> | null = null;
  let moveCount = $state(0); // seconds left in the undo countdown
  type MoveUndo = { kind: 'move'; map: Map<string, string> } | { kind: 'copy'; uids: string[] };
  let moveUndo: MoveUndo | null = null;

  function clearMoveTimers(): void {
    if (moveTimer) clearTimeout(moveTimer);
    moveTimer = null;
    if (moveInterval) clearInterval(moveInterval);
    moveInterval = null;
  }

  function pickLane(laneId: string): void {
    // The move/copy is applied immediately (so it's visible), then we run the
    // ConfirmButton-style sequence: ✓ holds, then a live "UNDO n" countdown.
    moveUndo = copyMode
      ? { kind: 'copy', uids: copyEventsToLane(selection.uids, laneId) }
      : { kind: 'move', map: moveEventsToLane(selection.uids, laneId) };
    moveMenuOpen = false;
    moveStage = 'done';
    clearMoveTimers();
    moveTimer = setTimeout(() => {
      moveTimer = null;
      moveStage = 'undo';
      moveCount = MOVE_UNDO_SECONDS;
      moveInterval = setInterval(() => {
        moveCount -= 1;
        if (moveCount <= 0) {
          clearMoveTimers();
          moveStage = 'idle';
          moveUndo = null;
        }
      }, 1000);
    }, MOVE_DONE_HOLD_MS);
  }

  function onMoveTap(): void {
    if (moveStage === 'done' || moveStage === 'undo') {
      // A tap anytime in the post-pick window reverses the move/copy.
      clearMoveTimers();
      if (moveUndo?.kind === 'copy') {
        deleteLocalEvents(moveUndo.uids);
      } else if (moveUndo?.kind === 'move') {
        const byLane = new Map<string, string[]>();
        for (const [uid, srcFeedId] of moveUndo.map) {
          if (!byLane.has(srcFeedId)) byLane.set(srcFeedId, []);
          byLane.get(srcFeedId)!.push(uid);
        }
        for (const [srcFeedId, uids] of byLane) moveEventsToLane(uids, srcFeedId);
      }
      moveUndo = null;
      moveStage = 'idle';
      return;
    }
    moveMenuOpen = !moveMenuOpen;
  }
  $effect(() => {
    if (!moveMenuOpen) return;
    const onPointer = (e: PointerEvent): void => {
      if (moveRoot && !moveRoot.contains(e.target as Node)) moveMenuOpen = false;
    };
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') moveMenuOpen = false; };
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('keydown', onKey);
    };
  });
  let fullyExpanded = $state(false);
  $effect(() => {
    if (!ui.statusExpanded || dragging) {
      fullyExpanded = false;
      return;
    }
    // Just past the height transition (--dur-ui, 150ms).
    const t = setTimeout(() => { fullyExpanded = true; }, 180);
    return () => clearTimeout(t);
  });

  // Kiosk mode keeps the tray collapsed and inert.
  $effect(() => {
    if (isKiosk()) {
      height = closedHeight;
      ui.statusExpanded = false;
      clearSelection();
    }
  });

  function maxHeight(): number {
    if (typeof window === 'undefined') return 400;
    return Math.round(window.innerHeight * (MAX_HEIGHT_VH / 100));
  }

  // The size readout shows until its fade ends (then unmounts): a fontSizeFlash
  // bump newer than the last one seen. The next event steps aside meanwhile.
  let readoutSeen = $state(0);
  const readoutOn = $derived(ui.fontSizeFlash > readoutSeen);
  const readoutText = $derived(fontSizeLabel(config.fontSize));

  // The element holding the drag's pointer capture, released if a pinch ends it.
  let dragCapture: { el: HTMLElement; id: number } | null = null;

  function startDrag(e: PointerEvent): void {
    // A second finger is a pinch (ui-size-gesture), never a drag of its own.
    if (!e.isPrimary) return;
    pressOn = pressedTarget(e);
    // Left mode is a plain click toggle (see the .handle onclick), never a
    // vertical height drag — bail before capturing the pointer.
    if (isKiosk() || leftMode) return;
    dragging = true;
    dragStartY = e.clientY;
    dragStartHeight = height;
    dragCapture = { el: e.currentTarget as HTMLElement, id: e.pointerId };
    dragCapture.el.setPointerCapture(e.pointerId);
  }

  // A second finger turns the press into a UI-size pinch: drop the handle
  // drag where it started, the tray body's armed swipe, and the tap either
  // might have become.
  function cancelDrag(): void {
    trayArmed = false;
    pressOn = null;
    if (!dragging) return;
    dragging = false;
    height = dragStartHeight;
    if (dragCapture?.el.hasPointerCapture(dragCapture.id)) dragCapture.el.releasePointerCapture(dragCapture.id);
    dragCapture = null;
  }

  function onDrag(e: PointerEvent): void {
    if (!dragging) return;
    const delta = dragStartY - e.clientY;
    const next = Math.min(maxHeight(), Math.max(closedHeight, dragStartHeight + delta));
    height = next;
  }

  function endDrag(e: PointerEvent): void {
    if (!dragging) return;
    dragging = false;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    const netDelta = e.clientY - dragStartY;
    // Only a tap opens What's new or the event; a drag that began there doesn't.
    if (Math.abs(netDelta) >= TAP_SLOP_PX) pressOn = null;
    // Released within the tap slop of the press → a tap, always toggle. Only a
    // clear drag past the slop resolves by direction below.
    if (Math.abs(netDelta) < TAP_SLOP_PX) {
      // A version / next-event tap waits for its click (onHandleClick).
      if (!pressOn) toggleExpand();
      return;
    }
    const startedExpanded = dragStartHeight > collapsedHeight + 2;
    const startedCollapsed = !startedExpanded;
    const draggedDown = netDelta > 0;
    const draggedUp = netDelta < 0;
    if (startedCollapsed && draggedUp) {
      height = maxHeight();
      lastExpandedHeight = height;
      ui.statusExpanded = true;
      trayExpand();
      return;
    }
    if (startedCollapsed) {
      height = closedHeight;
      ui.statusExpanded = false;
      return;
    }
    if (startedExpanded && draggedDown) {
      height = closedHeight;
      ui.statusExpanded = false;
      trayCollapse();
    } else if (height < collapsedHeight * 1.5) {
      height = closedHeight;
      ui.statusExpanded = false;
      trayCollapse();
    } else {
      lastExpandedHeight = height;
      ui.statusExpanded = true;
    }
  }

  // Swipe-down anywhere on the tray body drags it shut exactly like the header.
  // We arm only when the inner scroller is at the top; onTrayPointerMove then
  // locks the gesture's axis after a few px and hands a downward pull to the
  // same live drag (onDrag/endDrag) the handle uses, while letting sideways and
  // upward swipes scroll natively. A tap never reaches dragging, so row clicks
  // still fire.
  function onTrayPointerDown(e: PointerEvent): void {
    if (!e.isPrimary) return;
    trayArmed = false;
    // No swipe-down-to-dismiss on the side panel — it isn't anchored to the bottom.
    if (isKiosk() || leftMode) return;
    const scroller = (e.currentTarget as HTMLElement).querySelector<HTMLElement>(
      '.tray-scroll, .raw-block',
    );
    if (scroller && scroller.scrollTop > 0) return;
    trayArmed = true;
    trayArmStartY = e.clientY;
    trayArmStartX = e.clientX;
    trayArmTarget = e.currentTarget as HTMLElement;
    trayArmPointerId = e.pointerId;
  }

  function onTrayPointerMove(e: PointerEvent): void {
    if (dragging) {
      onDrag(e);
      return;
    }
    if (!trayArmed) return;
    const dy = e.clientY - trayArmStartY;
    const dx = Math.abs(e.clientX - trayArmStartX);
    // Wait until the gesture has travelled enough to read its direction.
    if (Math.max(dx, Math.abs(dy)) < AXIS_LOCK_PX) return;
    // Upward or sideways-dominant motion is a scroll (vertical content scroll,
    // or the horizontal raw table / filter chips) — release it to the browser.
    if (dy <= dx) {
      trayArmed = false;
      return;
    }
    // Downward, vertical-dominant: hand off to the same live drag the header
    // runs, mapped from the touch origin so the tray follows the finger 1:1 and
    // endDrag snaps it open/closed (the retry).
    trayArmed = false;
    dragging = true;
    dragStartY = trayArmStartY;
    dragStartHeight = height;
    if (trayArmTarget) {
      dragCapture = { el: trayArmTarget, id: trayArmPointerId };
      trayArmTarget.setPointerCapture(trayArmPointerId);
    }
    onDrag(e);
  }

  function onTrayPointerUp(e: PointerEvent): void {
    trayArmed = false;
    if (!dragging) return;
    endDrag(e);
  }

  function toggleExpand(): void {
    if (isKiosk()) return;
    // Left mode: the bar stays collapsed; only the side panel opens/closes.
    if (leftMode) {
      ui.statusExpanded = !ui.statusExpanded;
      if (ui.statusExpanded) trayExpand(); else trayCollapse();
      return;
    }
    // Decide from the real open state, not the live `height` — a tap can nudge
    // `height` a few px past the collapsed threshold, which would otherwise make
    // `expanded` read true and toggle the wrong way (a click that never opens).
    if (ui.statusExpanded) {
      height = closedHeight;
      ui.statusExpanded = false;
      trayCollapse();
    } else {
      height = lastExpandedHeight > collapsedHeight + 2 ? lastExpandedHeight : maxHeight();
      ui.statusExpanded = true;
      trayExpand();
    }
  }

  // Base date: temp marker or today
  const baseDate = $derived(
    ui.tempMarkerMs != null ? startOfDay(new Date(ui.tempMarkerMs)) : today.value
  );
  // The tray's window end. A duration marker sets it exactly — the day after its
  // inclusive last day — so the tray lists precisely the marked span; otherwise
  // it keeps the default one month forward. Computed once here and read by the
  // grouping and the filter-chip counts, so the list and its counts can't drift.
  const windowEnd = $derived(
    markerIsSpan()
      ? addDays(startOfDay(new Date(ui.tempMarkerEndMs!)), 1)
      : addMonths(baseDate, 1)
  );
  // Same window as a date to SHOW: the last day it covers, not the exclusive
  // bound one day past it.
  const windowLastDay = $derived(addDays(windowEnd, -1));
  // The window's edges (base day, the day after it, windowEnd) as instants to
  // compare an event against: UTC midnights for all-day events, the moment each
  // day begins in the display zone for timed ones — so at 00:10 in Athens a
  // 01:00 event is today's, though its UTC instant is still yesterday.
  function windowBounds(base: Date): (allDay: boolean) => { base: number; todayEnd: number; end: number } {
    const day = base.getTime();
    const edges = (allDay: boolean) => ({
      base: dayStartMs(day, allDay),
      todayEnd: dayStartMs(day + MS_PER_DAY, allDay),
      end: dayStartMs(windowEnd.getTime(), allDay),
    });
    const allDay = edges(true);
    const timed = edges(false);
    return (isAllDay) => (isAllDay ? allDay : timed);
  }

  // What's next for the collapsed status line (category 'none' feeds only): an
  // event under way with its time left, else the next to start (pickStatusEvent).
  // The Next Event setting: everything, all-day or timed events only, or none.
  const statusEvent = $derived.by<StatusEvent | null>(() => {
    if (config.nextEvents === 'none') return null;
    const byFeed = getDisplayByFeed();
    const candidates: DisplayEvent[] = [];
    for (const feed of config.feeds) {
      if (feed.hidden) continue;
      if (feed.category !== 'none') continue;
      if (feed.source.kind === 'scratchpad') continue; // never surface Draft events here
      for (const ev of (byFeed[feed.id] ?? [])) candidates.push(ev);
    }
    return pickStatusEvent(candidates, clock.now, config.nextEvents, today.value.getTime());
  });
  const nextEvent = $derived(statusEvent?.event ?? null);

  const nextEventLabel = $derived.by<string | null>(() => {
    if (!statusEvent) return null;
    const { event, ongoing } = statusEvent;
    if (ongoing) return 'NOW · ' + formatTimeLeft(event.end.getTime(), clock.now) + ' · ' + event.displayTitle;
    // Days counted in the display zone, not UTC (01:00 in Athens is still today).
    const startDayMs = event.allDay
      ? event.start.getTime()
      : zonedDateProxy(event.start, config.timezone).setUTCHours(0, 0, 0, 0);
    const rel = formatNextRelative(event.start, clock.now, { startDayMs, todayMs: today.value.getTime() });
    if (event.allDay) return rel + ' · ' + event.displayTitle;
    const time = formatTime(event.start, config.timeFormat, config.timezone);
    return rel + ' · ' + time + ' · ' + event.displayTitle;
  });

  // Marquee the next-event label when it's wider than the status bar: hold it
  // still for 2s (readable start), then scroll continuously. Two copies with a
  // fixed gap make the loop seamless — the shift is one copy plus that gap.
  const MARQUEE_GAP_PX = 48; // must match the .next-event-track gap in CSS
  const MARQUEE_SPEED_PX_S = 45;
  let nextEventEl = $state<HTMLElement>();
  let nextEventCopyEl = $state<HTMLElement>();
  let marquee = $state<{ on: boolean; shift: number; dur: number }>({ on: false, shift: 0, dur: 0 });

  $effect(() => {
    nextEventLabel; // re-measure when the text changes
    const container = nextEventEl;
    const copy = nextEventCopyEl;
    if (!container || !copy) return;
    const measure = (): void => {
      const on = copy.scrollWidth - container.clientWidth > 4;
      const shift = on ? copy.scrollWidth + MARQUEE_GAP_PX : 0;
      const cur = untrack(() => marquee);
      if (on !== cur.on || shift !== cur.shift) {
        marquee = { on, shift, dur: shift / MARQUEE_SPEED_PX_S };
      }
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  });

  // Helpers for event groups
  function getWeekStart(d: Date): Date {
    if (config.weekStart === 'sunday') {
      return addDays(startOfDay(d), -d.getUTCDay());
    }
    const dow = d.getUTCDay() || 7;
    return addDays(startOfDay(d), 1 - dow);
  }

  // The tray's week heading. Under a duration marker (`span`, inclusive
  // UTC-midnight days) the heading covers only the marked part of the week and
  // leads with that many days — "5D · AUG 5–9, 2026 (W32)" for a marker opening
  // on the Wednesday. The ISO week number always comes from the real week start.
  function formatWeekLabel(
    weekStart: Date,
    span: { startMs: number; endMs: number } | null = null,
  ): string {
    const weekEnd = addDays(weekStart, 6);
    // Standard ISO week number, independent of the monday/sunday setting.
    const wn = isoWeekNumber(weekStart);
    const clip =
      span == null
        ? null
        : intersectDaySpan(weekStart.getTime(), weekEnd.getTime(), span.startMs, span.endMs);
    const from = clip == null ? weekStart : new Date(clip.startMs);
    const to = clip == null ? weekEnd : new Date(clip.endMs);
    const sd = from.getUTCDate();
    const ed = to.getUTCDate();
    const sy = from.getUTCFullYear();
    const ey = to.getUTCFullYear();
    const sm = formatMonth(from, config.locale, 'short');
    const em = formatMonth(to, config.locale, 'short');
    let range: string;
    if (sd === ed && sy === ey && from.getUTCMonth() === to.getUTCMonth()) {
      range = `${sm} ${sd}, ${sy}`;
    } else if (from.getUTCMonth() === to.getUTCMonth() && sy === ey) {
      range = `${sm} ${sd}–${ed}, ${sy}`;
    } else if (sy === ey) {
      range = `${sm} ${sd}–${em} ${ed}, ${sy}`;
    } else {
      range = `${sm} ${sd} ${sy}–${em} ${ed} ${ey}`;
    }
    const days = clip == null ? '' : formatDayCount(clip.days, config.locale) + ' · ';
    return `${days}${range} (W${wn})`;
  }

  type EventWithFeed = { event: DisplayEvent; feedId: string; feedName: string; inferredCity: string | null };

  function cityFromTz(feedId: string): string | null {
    const tz = effectiveFeedTz(feedId);
    if (!tz || tz === 'local' || tz === 'UTC') return null;
    return tz.split('/').pop()?.replace(/_/g, ' ') ?? null;
  }

  const CATEGORY_ORDER: FeedCategory[] = [
    'none', 'events', 'holidays', 'observances', 'operations', 'announcements', 'guests',
    'travel-local', 'travel-international',
  ];
  const CATEGORY_LABELS: Record<FeedCategory, string> = {
    none: 'Auto',
    events: 'Events',
    holidays: 'Holidays',
    observances: 'Observances',
    operations: 'Operations',
    announcements: 'Announcements',
    guests: 'Guests',
    'travel-local': 'Travel (Local)',
    'travel-international': 'Travel (International)',
  };

  type CategoryGroup = { category: FeedCategory; label: string; items: EventWithFeed[] };
  type WeekGroup = { label: string; categories: CategoryGroup[] };

  function groupByCategory(items: EventWithFeed[]): CategoryGroup[] {
    const map = new Map<FeedCategory, EventWithFeed[]>();
    const feedCats = new Map(config.feeds.map((f) => [f.id, f.category]));
    for (const ef of items) {
      const feedCat = feedCats.get(ef.feedId) ?? 'none';
      // One bucket per event: explicit event.category trumps rule-derived
      // ruleCategory, which trumps the feed's category.
      const cat: FeedCategory = ef.event.category ?? ef.event.ruleCategory ?? feedCat;
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(ef);
    }
    return CATEGORY_ORDER
      .filter(c => map.has(c) && config.trayFilter.categories.includes(c))
      .map(c => ({ category: c, label: CATEGORY_LABELS[c], items: map.get(c)! }));
  }

  // Event groups — only computed when tray is open
  const eventGroups = $derived.by<{
    spanTitle: string | null;
    todayLabel: string;
    todayCategories: CategoryGroup[];
    weeks: WeekGroup[];
  } | null>(() => {
    // Only compute the (potentially large) grouping while the tray is actually
    // shown. `trayOpen` covers both bottom mode (dragged height) and left mode
    // (the explicit expand flag); selection mode always shows the tray too.
    if (!trayOpen && !inSelectionMode) return null;

    const base = baseDate;
    const bounds = windowBounds(base);
    const byFeed = getDisplayByFeed();
    const inSelection = selection.mode && selection.uids.size > 0;
    // A duration marker retitles the whole list with its span and clips the week
    // headings to it; with no marker (or a single-day one) the tray keeps its
    // leading Today/date section and plain calendar weeks. Selected events can
    // sit outside the span, so selection mode never clips.
    const span = !inSelection && markerIsSpan() ? markerRange() : null;
    const spanTitle =
      span == null
        ? null
        : formatSpanLabel(span.startMs, span.endMs, config.dateFormat, config.locale);

    const todayItems: EventWithFeed[] = [];
    const futureItems: EventWithFeed[] = [];

    for (const feed of config.feeds) {
      if (feed.hidden) continue;
      // Per feed, not per event: most of the range's events are filtered out.
      const inferredCity = cityFromTz(feed.id);
      const withFeed = (event: DisplayEvent): EventWithFeed =>
        ({ event, feedId: feed.id, feedName: feed.name, inferredCity });
      for (const ev of (byFeed[feed.id] ?? [])) {
        if (ev.hidden) continue;
        if (hiddenLocations.size > 0 && ev.displayLocation && hiddenLocations.has(ev.displayLocation)) continue;
        if (inSelection) {
          // Group all selected events by week + type, like the normal tray.
          if (selection.uids.has(ev.uid)) futureItems.push(withFeed(ev));
          continue;
        }
        // An event happening right now belongs in Today even if its clock end
        // fell before the UTC day-start (the pre-dawn window) or it started
        // days ago (a combined/multi-day bar still running) — only when the
        // base really is today, not a temp-marker preview of another day.
        const ongoingNow =
          ui.tempMarkerMs == null &&
          ev.start.getTime() <= clock.now &&
          clock.now < ev.end.getTime();
        // Under a duration marker every day of the span is "a week's worth" —
        // the start day gets no section of its own, it just heads its week.
        const { base: b, todayEnd: te, end: we } = bounds(ev.allDay);
        const s = ev.start.getTime();
        const e = ev.end.getTime();
        if (span == null && ((s < te && e > b) || ongoingNow)) {
          todayItems.push(withFeed(ev));
        } else if (s >= (span == null ? te : b) && s < we) {
          futureItems.push(withFeed(ev));
        } else if (span != null && s < b && e > b) {
          // A bar already running when the span opens still belongs to it.
          futureItems.push(withFeed(ev));
        }
      }
    }

    todayItems.sort((a, b) => {
      if (a.event.allDay && !b.event.allDay) return -1;
      if (!a.event.allDay && b.event.allDay) return 1;
      return a.event.start.getTime() - b.event.start.getTime();
    });
    futureItems.sort((a, b) => a.event.start.getTime() - b.event.start.getTime());

    const weekMap = new Map<string, EventWithFeed[]>();
    const weekStartList: Date[] = [];
    for (const ef of futureItems) {
      // An event already running when the span opens is filed under the span's
      // first week, so no heading can fall outside the marked days.
      // A timed event files under its day in the display zone, not its UTC day.
      const anchor =
        span != null && ef.event.start.getTime() < bounds(ef.event.allDay).base
          ? new Date(span.startMs)
          : ef.event.allDay
            ? ef.event.start
            : zonedDateProxy(ef.event.start, config.timezone);
      const ws = getWeekStart(anchor);
      const key = ws.toISOString();
      if (!weekMap.has(key)) {
        weekMap.set(key, []);
        weekStartList.push(ws);
      }
      weekMap.get(key)!.push(ef);
    }

    const weeks: WeekGroup[] = weekStartList
      .map(ws => ({
        label: formatWeekLabel(ws, span),
        categories: groupByCategory(weekMap.get(ws.toISOString())!),
      }))
      // Hide weeks with no visible events (all filtered out).
      .filter(w => w.categories.length > 0);

    const todayLabel = ui.tempMarkerMs != null
      ? `${formatDateLong(base, config.locale)} (W${isoWeekNumber(base)})`
      : `Today (W${isoWeekNumber(base)})`;

    return { spanTitle, todayLabel, todayCategories: groupByCategory(todayItems), weeks };
  });

  function eventTimeLabel(ev: DisplayEvent): string {
    if (!ev.allDay) {
      return formatTime(ev.start, config.timeFormat, config.timezone)
        + '—'
        + formatTime(ev.end, config.timeFormat, config.timezone);
    }
    const days = durationDays(ev.start, ev.end);
    const last = new Date(ev.end.getTime() - 1);
    const sd = ev.start.getUTCDate();
    const ed = last.getUTCDate();
    const sm = formatMonth(ev.start, config.locale, 'short');
    const em = formatMonth(last, config.locale, 'short');
    if (days === 1) return `${sd} ${sm}`;
    if (ev.start.getUTCMonth() === last.getUTCMonth()) return `${sd}—${ed} ${sm}`;
    return `${sd} ${sm}—${ed} ${em}`;
  }

  function openEvent(ef: EventWithFeed): void {
    ui.modalEvent = ef.event;
    window.dispatchEvent(
      new CustomEvent('cal:scroll-to-date', { detail: { date: ef.event.start, utcDay: ef.event.allDay } }),
    );
  }

  // Window counts — computed from all events in the window, no filters applied
  const windowCounts = $derived.by<{
    categories: Map<FeedCategory, number>;
    locations: Array<{ loc: string; count: number }>;
  } | null>(() => {
    if (!trayOpen) return null;
    const bounds = windowBounds(baseDate);
    const byFeed = getDisplayByFeed();
    const catCounts = new Map<FeedCategory, number>();
    const locCounts = new Map<string, number>();
    for (const feed of config.feeds) {
      if (feed.hidden) continue;
      const feedCat = feed.category ?? 'none';
      for (const ev of (byFeed[feed.id] ?? [])) {
        if (ev.hidden) continue;
        const { base: b, todayEnd: te, end: we } = bounds(ev.allDay);
        const s = ev.start.getTime();
        const inWindow = (s < te && ev.end.getTime() > b) || (s >= te && s < we);
        if (!inWindow) continue;
        const cat: FeedCategory = ev.category ?? ev.ruleCategory ?? feedCat;
        catCounts.set(cat, (catCounts.get(cat) ?? 0) + 1);
        if (ev.displayLocation) {
          locCounts.set(ev.displayLocation, (locCounts.get(ev.displayLocation) ?? 0) + 1);
        }
      }
    }
    const locations = [...locCounts.entries()]
      .map(([loc, count]) => ({ loc, count }))
      .sort((a, b) => b.count - a.count);
    return { categories: catCounts, locations };
  });

  // Total in-window events — shown on the "Types" clear tag.
  const windowTotal = $derived.by(() => {
    if (!windowCounts) return 0;
    let n = 0;
    for (const v of windowCounts.categories.values()) n += v;
    return n;
  });

  // Filter panel
  let filterOpen = $state(false);
  let hiddenLocations = $state(new Set<string>());

  const isFilterActive = $derived(
    config.trayFilter.categories.length < CATEGORY_ORDER.length ||
    hiddenLocations.size > 0,
  );

  $effect(() => { if (!trayOpen) filterOpen = false; });

  const visibleEventCount = $derived.by<number>(() => {
    if (!eventGroups) return 0;
    let n = 0;
    for (const c of eventGroups.todayCategories) n += c.items.length;
    for (const w of eventGroups.weeks) for (const c of w.categories) n += c.items.length;
    return n;
  });

  const totalEventCount = $derived.by<number>(() => {
    if (!windowCounts) return 0;
    let total = 0;
    for (const count of windowCounts.categories.values()) total += count;
    return total;
  });

  function toggleCategory(cat: FeedCategory): void {
    const cats = config.trayFilter.categories;
    config.trayFilter = {
      ...config.trayFilter,
      categories: cats.includes(cat) ? cats.filter(c => c !== cat) : [...cats, cat],
    };
  }

  function clearCategoryFilter(): void {
    config.trayFilter = {
      ...config.trayFilter,
      categories: [...CATEGORY_ORDER],
    };
  }

  function toggleLocation(loc: string): void {
    const next = new Set(hiddenLocations);
    if (next.has(loc)) next.delete(loc); else next.add(loc);
    hiddenLocations = next;
  }

  function clearLocationFilter(): void {
    hiddenLocations = new Set();
  }

  // Raw mode toggle
  let rawMode = $state(false);
  let copyDone = $state(false);

  function gatherTrayEvents(): ParsedEvent[] {
    if (!eventGroups) return [];
    const out: ParsedEvent[] = [];
    for (const cat of eventGroups.todayCategories) for (const ef of cat.items) out.push(ef.event);
    for (const week of eventGroups.weeks) for (const cat of week.categories) for (const ef of cat.items) out.push(ef.event);
    return out;
  }

  const trayEvents = $derived(gatherTrayEvents());

  $effect(() => {
    if (typeof window === 'undefined') return;
    const handler = (): void => toggleExpand();
    window.addEventListener('cal:toggle-status', handler);
    return () => window.removeEventListener('cal:toggle-status', handler);
  });

  // Raw export columns shared by the table display and the TSV copy.
  const RAW_COLUMNS = ['Start Date', 'End Date', 'Start Time', 'End Time', 'Title', 'Location', 'Category'];
  // On-screen width cap per column, in characters (dates and times always fit);
  // longer text is clipped with an ellipsis by CSS only, so the copied TSV
  // (built from rawRows) and a hand selection keep the full text.
  const RAW_COLUMN_MAX_CH: (number | null)[] = [null, null, null, null, 40, 28, 16];
  // Structured rows — drives both the raw-mode table and the TSV text below.
  const rawRows = $derived.by<string[][]>(() => {
    if (!eventGroups) return [];
    const out: string[][] = [];
    function addItems(items: EventWithFeed[], categoryLabel: string): void {
      for (const ef of items) {
        const ev = ef.event;
        const startDate = formatDate(
          ev.allDay ? ev.start : zonedDateProxy(ev.start, config.timezone),
          config.dateFormat,
          config.locale,
        );
        const endRaw = ev.allDay
          ? new Date(ev.end.getTime() - 1)
          : zonedDateProxy(ev.end, config.timezone);
        const endDate = formatDate(endRaw, config.dateFormat, config.locale);
        const startTime = ev.allDay ? '' : formatTime(ev.start, config.timeFormat, config.timezone);
        const endTime = ev.allDay ? '' : formatTime(ev.end, config.timeFormat, config.timezone);
        const location = ev.displayLocation || ef.inferredCity || '';
        out.push([startDate, endDate, startTime, endTime, ev.displayTitle, location, categoryLabel]);
      }
    }
    for (const cat of eventGroups.todayCategories) addItems(cat.items, cat.label);
    for (const week of eventGroups.weeks)
      for (const cat of week.categories) addItems(cat.items, cat.label);
    return out;
  });

  // TSV text — derived so it updates reactively and can be copied.
  const tsvText = $derived(
    [RAW_COLUMNS.join('\t'), ...rawRows.map((r) => r.join('\t'))].join('\n'),
  );

  async function copyContent(): Promise<void> {
    try {
      if (rawMode) {
        await navigator.clipboard.writeText(tsvText);
      } else {
        if (!eventGroups) return;
        const groups = eventGroups;
        const lines: string[] = [];
        function esc(s: string): string {
          return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        }
        function addSection(label: string, cats: typeof groups.todayCategories): void {
          lines.push(`<b>${esc(label)}</b>`);
          for (const cat of cats) {
            lines.push(`<i>${esc(cat.label)}</i>`);
            lines.push('<ul>');
            for (const ef of cat.items) {
              const time = esc(eventTimeLabel(ef.event));
              const title = esc(ef.event.displayTitle);
              const loc = ef.event.displayLocation || ef.inferredCity || '';
              const locPart = loc ? ` · ${esc(loc)}` : '';
              lines.push(`<li>${time} · ${title}${locPart}</li>`);
            }
            lines.push('</ul>');
          }
        }
        if (groups.todayCategories.length > 0) addSection(groups.todayLabel, groups.todayCategories);
        for (const week of groups.weeks) addSection(week.label, week.categories);
        const html = lines.join('\n');
        if (typeof ClipboardItem !== 'undefined') {
          const blob = new Blob([html], { type: 'text/html' });
          await navigator.clipboard.write([new ClipboardItem({ 'text/html': blob })]);
        } else {
          await navigator.clipboard.writeText(html);
        }
      }
      copyDone = true;
      setTimeout(() => { copyDone = false; }, 2000);
    } catch {
      pushLog('Copy failed', 'error');
    }
  }
</script>

<aside
  class="status-bar"
  style="height: {height}px;"
  data-expanded={expanded ? 'true' : null}
  use:uiSizeGesture={{ onStep: stepFontSize, enabled: canResizeUi, onPinchStart: cancelDrag }}
>
  <!-- A font-size change (setFontSize): the new size ("100%" at the default) in
       the bar's top-left corner, where the next event is hidden meanwhile;
       holds --dur-readout, then fades. Keyed so a repeat restarts. -->
  {#key ui.fontSizeFlash}
    {#if readoutOn}
      <div class="size-readout" aria-hidden="true" onanimationend={() => (readoutSeen = ui.fontSizeFlash)}>{readoutText}</div>
    {/if}
  {/key}
  <!-- Always mounted, so screen readers announce the text as it changes. -->
  <span class="sr-status" role="status">{readoutOn ? readoutText : ''}</span>
  {#if inSelectionMode}
    <div
      class="handle selection-head"
      role="presentation"
      bind:clientHeight={collapsedHeight}
      onpointerdown={startDrag}
      onpointermove={onDrag}
      onpointerup={endDrag}
      onpointercancel={endDrag}
    >
      <ConfirmButton
        label="DELETE"
        variant="delete"
        height={28}
        hpad="0.95em"
        disabled={!hasLocalSelection}
        idleTitle="Delete selected"
        doneTitle="Tap to undo"
        onCommit={commitDelete}
        onpointerdown={(e) => e.stopPropagation()}
      />
      <div class="move-menu" bind:this={moveRoot}>
        <button
          type="button"
          class="sel-btn sel-move"
          class:done={moveStage === 'done'}
          class:undo={moveStage === 'undo'}
          aria-haspopup="menu"
          aria-expanded={moveMenuOpen}
          title={moveStage !== 'idle' ? 'Tap to undo' : copyMode ? 'Copy selected to lane' : 'Move selected to lane'}
          onpointerdown={(e) => e.stopPropagation()}
          onclick={onMoveTap}
        ><span class="sel-stack"><span class="sel-sizer" aria-hidden="true">COPY&nbsp;<span class="sel-icon-box"></span></span><span class="sel-current">{moveStage === 'undo' ? 'UNDO' : copyMode ? 'COPY' : 'MOVE'}{#if moveStage === 'undo'}&nbsp;<span class="sel-count">{moveCount}</span>{:else if moveStage === 'done'}&nbsp;<Icon name="check" size={13} />{/if}</span></span></button>
        {#if moveMenuOpen}
          <div class="move-menu-list" role="menu">
            {#each localLanes as lane (lane.id)}
              <button
                type="button"
                role="menuitem"
                class="move-menu-item"
                onpointerdown={(e) => e.stopPropagation()}
                onclick={() => pickLane(lane.id)}
              >{lane.name}</button>
            {/each}
          </div>
        {/if}
      </div>
      <span class="sel-count">{mixedSelection ? `${selectedLocalUids.length} / ${selTotal}` : selTotal}</span>
      <span class="sel-cancel-wrap">
        <ConfirmButton
          label="CANCEL"
          variant="neutral"
          stages={2}
          height={28}
          hpad="0.95em"
          idleTitle="Cancel selection"
          confirmTitle="Tap again to cancel"
          onConfirm={commitCancel}
          onpointerdown={(e) => e.stopPropagation()}
        />
      </span>
    </div>
  {:else if undoBar.message}
    <!-- After a drag / resize / nudge: what changed, with UNDO and CANCEL (dismiss)
         — in the slot the multi-select actions use. No timer: it stays until
         another action (see the effect above). -->
    <div
      class="handle selection-head undo-head"
      role="status"
      bind:this={undoBarEl}
      bind:clientHeight={collapsedHeight}
      onpointerdown={startDrag}
      onpointermove={onDrag}
      onpointerup={endDrag}
      onpointercancel={endDrag}
    >
      <span class="undo-message" title={undoBar.message}>{undoBar.message}</span>
      {#if undoBar.canUndo}
        <button
          type="button"
          class="sel-btn"
          title={undoBar.next ? `Undo also: ${undoBar.next} (Ctrl/⌘+Z)` : 'Undo (Ctrl/⌘+Z)'}
          onpointerdown={(e) => e.stopPropagation()}
          onclick={() => undoLastChange()}
        >UNDO</button>
      {/if}
      <button
        type="button"
        class="sel-btn"
        title="Dismiss"
        onpointerdown={(e) => e.stopPropagation()}
        onclick={dismissUndoBar}
      >CANCEL</button>
    </div>
  {:else}
    <button
      type="button"
      class="handle"
      aria-label={trayOpen ? 'Collapse events' : 'Expand events'}
      aria-expanded={trayOpen}
      bind:clientHeight={collapsedHeight}
      onpointerdown={startDrag}
      onpointermove={onDrag}
      onpointerup={endDrag}
      onpointercancel={endDrag}
      onclick={onHandleClick}
    >
      <span class="status-line status-line-left">
        {#if nextEventLabel}
          <span class="next-event" style:visibility={readoutOn ? 'hidden' : null} bind:this={nextEventEl} title={nextEvent ? 'Open ' + nextEvent.displayTitle : undefined}>
            {#if marquee.on}
              <span
                class="next-event-track marquee"
                style="--marquee-shift: {marquee.shift}px; --marquee-dur: {marquee.dur}s;"
              >
                <span class="next-event-copy" bind:this={nextEventCopyEl}>{nextEventLabel}</span>
                <span class="next-event-copy" aria-hidden="true">{nextEventLabel}</span>
              </span>
            {:else}
              <span class="next-event-copy next-event-static" bind:this={nextEventCopyEl}>{nextEventLabel}</span>
            {/if}
          </span>
        {/if}
      </span>
      {#if !isKiosk()}
        <span class="toggle" aria-hidden="true" style="transform: rotate({toggleDeg}deg)">
          <Icon name="chevron-up" size={16} />
        </span>
      {:else}
        <span aria-hidden="true"></span>
      {/if}
      <span class="status-line status-line-right">
        <span
          class="status-chip"
          data-online={online.value ? 'true' : null}
          data-updating={swStatus.updating ? 'true' : null}
          title={`${swStatus.updating ? 'Updating' : online.value ? 'Online' : 'Offline'} · What’s New in v${__APP_VERSION__}`}
        >
          <span class="dot" aria-hidden="true"></span>
          <!-- While a new version installs the chip keeps the running one's number. -->
          <span class="status-text">{showVersion || swStatus.updating ? `v${__APP_VERSION__}` : online.value ? 'ONLINE' : 'OFFLINE'}</span>
        </span>
      </span>
    </button>
  {/if}

  {#if leftMode || (eventGroups && (trayOpen || inSelectionMode))}
    <!-- In left mode the panel stays mounted (empty when closed) so the slide-in
         is a pure CSS transform; the heavy content only renders while open. In
         bottom mode it mounts on open exactly as before. -->
    <div
      class="events-tray"
      class:side-left={leftMode}
      role="region"
      aria-label="Upcoming events"
      data-open={sidePanelShown ? 'true' : null}
      inert={leftMode ? !sidePanelShown : !fullyExpanded}
      style={leftMode ? `top: 0; bottom: ${closedHeight}px` : undefined}
      onpointerdown={onTrayPointerDown}
      onpointermove={onTrayPointerMove}
      onpointerup={onTrayPointerUp}
      onpointercancel={onTrayPointerUp}
    >
      {#if eventGroups}
      {#if rawMode}
        <div class="raw-block">
          <table class="raw-table">
            <thead>
              <tr>{#each RAW_COLUMNS as col, c (col)}<th><span class="raw-cell" style:max-width={RAW_COLUMN_MAX_CH[c] ? `${RAW_COLUMN_MAX_CH[c]}ch` : null}>{col}</span></th>{/each}</tr>
            </thead>
            <tbody>
              {#each rawRows as row, i (i)}
                <tr>{#each row as cell, c (c)}<td><span class="raw-cell" style:max-width={RAW_COLUMN_MAX_CH[c] ? `${RAW_COLUMN_MAX_CH[c]}ch` : null} title={RAW_COLUMN_MAX_CH[c] && cell.length > RAW_COLUMN_MAX_CH[c]! ? cell : null}>{cell}</span></td>{/each}</tr>
              {/each}
            </tbody>
          </table>
        </div>
      {:else}
        <div class="tray-scroll">
          {#if eventGroups.todayCategories.length === 0 && eventGroups.weeks.length === 0}
            <p class="empty">No events from {formatDate(baseDate, config.dateFormat, config.locale)} to {formatDate(windowLastDay, config.dateFormat, config.locale)}.</p>
          {:else}
            {#if eventGroups.spanTitle}
              <!-- The marked span itself, over the week groups it contains. -->
              <h2 class="span-title" data-mono>{eventGroups.spanTitle}</h2>
            {/if}
            {#if eventGroups.todayCategories.length > 0}
              <div class="week-group">
                <h2 class="week-label">{eventGroups.todayLabel}</h2>
                {#each eventGroups.todayCategories as catGroup (catGroup.category)}
                  <div class="cat-group">
                    {#if catGroup.category !== 'none'}
                      <h3 class="cat-label">{catGroup.label}</h3>
                    {/if}
                    <div class="event-list">
                      {#each catGroup.items as ef (ef.feedId + ':' + ef.event.uid)}
                        <button type="button" class="event-row" onclick={() => openEvent(ef)}>
                          <span class="event-time">{eventTimeLabel(ef.event)}</span>
                          <span class="event-title">{titleGlyphs(ef.event.displayTitle)}</span>
                          {#if ef.event.displayLocation || ef.inferredCity}
                            <span class="event-loc">{ef.event.displayLocation || ef.inferredCity}</span>
                          {/if}
                        </button>
                      {/each}
                    </div>
                  </div>
                {/each}
              </div>
            {/if}

            {#each eventGroups.weeks as week}
              <div class="week-group">
                <h2 class="week-label">{week.label}</h2>
                {#each week.categories as catGroup (catGroup.category)}
                  <div class="cat-group">
                    {#if catGroup.category !== 'none'}
                      <h3 class="cat-label">{catGroup.label}</h3>
                    {/if}
                    <div class="event-list">
                      {#each catGroup.items as ef (ef.feedId + ':' + ef.event.uid)}
                        <button type="button" class="event-row" onclick={() => openEvent(ef)}>
                          <span class="event-time">{eventTimeLabel(ef.event)}</span>
                          <span class="event-title">{titleGlyphs(ef.event.displayTitle)}</span>
                          {#if ef.event.displayLocation || ef.inferredCity}
                            <span class="event-loc">{ef.event.displayLocation || ef.inferredCity}</span>
                          {/if}
                        </button>
                      {/each}
                    </div>
                  </div>
                {/each}
              </div>
            {/each}
          {/if}
        </div>
      {/if}
      {#if filterOpen}
        <div class="filter-panel">
          <div class="filter-row">
            <button
              type="button"
              class="filter-clear"
              data-active={config.trayFilter.categories.length < CATEGORY_ORDER.length ? 'true' : null}
              onclick={clearCategoryFilter}
              title="Show all types"
            >Types ({windowTotal})</button>
            {#each CATEGORY_ORDER as cat}
              {@const catCount = windowCounts?.categories.get(cat) ?? 0}
              <button
                type="button"
                class="filter-chip"
                aria-pressed={config.trayFilter.categories.includes(cat)}
                onclick={() => toggleCategory(cat)}
              >{CATEGORY_LABELS[cat]}{catCount > 0 ? ` (${catCount})` : ''}</button>
            {/each}
          </div>
          {#if windowCounts && windowCounts.locations.length > 0}
            <div class="filter-row">
              <button
                type="button"
                class="filter-clear"
                data-active={hiddenLocations.size > 0 ? 'true' : null}
                onclick={clearLocationFilter}
                title="Show all locations"
              >Location</button>
              {#each windowCounts.locations as { loc, count } (loc)}
                <button
                  type="button"
                  class="filter-chip"
                  aria-pressed={!hiddenLocations.has(loc)}
                  onclick={() => toggleLocation(loc)}
                >{loc} ({count})</button>
              {/each}
            </div>
          {/if}
        </div>
      {/if}
      <div class="copy-bar">
        <button
          type="button"
          class="copy-btn"
          aria-pressed={filterOpen}
          data-filter-active={isFilterActive && !filterOpen ? 'true' : null}
          onclick={() => (filterOpen = !filterOpen)}
          title="Filter visible types and locations"
        >Filter</button>
        <span class="event-counter" data-mono>{visibleEventCount} / {totalEventCount}</span>
        <span class="copy-spacer"></span>
        <button
          type="button"
          class="copy-btn"
          data-toggle="true"
          aria-pressed={rawMode}
          onclick={() => (rawMode = !rawMode)}
          title="Toggle table view"
          aria-label="Toggle table view"
        ><Icon name="table-split" size={16} /></button>
        <CalendarDownloadMenu events={trayEvents} disabled={isKiosk()} />
        <CopyIconButton
          copied={copyDone}
          label={rawMode ? 'Copy as tab-separated list' : 'Copy as rich text'}
          onclick={() => void copyContent()}
        />
      </div>
      {/if}
    </div>
  {/if}
</aside>

<style>
  .size-readout {
    position: absolute;
    top: 0;
    left: max(6px, var(--time-header-pad-x));
    /* Centred in the bar's row, in the next event's place (hidden meanwhile). */
    height: var(--tray-header-h);
    display: flex;
    align-items: center;
    z-index: 2;
    color: var(--ink-color);
    pointer-events: none;
    animation: size-readout-fade var(--dur-ink) ease-in var(--dur-readout) forwards;
  }
  .sr-status {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
  @keyframes size-readout-fade {
    to {
      opacity: 0;
    }
  }
  .status-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: var(--z-bar);
    display: flex;
    flex-direction: column;
    border-top: calc(var(--border-w) + 1px) solid var(--ink-color);
    background: var(--paper-color);
    color: var(--ink-color);
    user-select: none;
    -webkit-user-select: none;
    /* Pinch is ours (UI size, ui-size-gesture.ts), never the page's zoom. */
    touch-action: pan-x pan-y;
    transition: height var(--dur-ui) ease-out;
  }
  .status-bar[data-expanded='true'] .handle {
    border-bottom: var(--border-w) dashed var(--ink-color);
  }
  .status-bar[data-expanded='true'] {
    transition: none;
  }
  .handle {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: var(--toolbar-gap);
    /* Height tracks the spacing setting; multi-select uses the taller
       .selection-head instead. collapsedHeight is measured from this. */
    height: var(--tray-header-h);
    flex-shrink: 0;
    padding: 0 var(--time-header-pad-x);
    border: 0;
    background: transparent;
    color: inherit;
    text-align: left;
    cursor: pointer;
    touch-action: none;
  }
  .status-line {
    display: inline-flex;
    align-items: center;
    gap: 0.6em;
    overflow: hidden;
    font-size: var(--fs-12);
    min-width: 0;
  }
  /* Next-event text sits on the left; the online pill on the right. The centre
     toggle stays centred via the grid's auto column. */
  .status-line-left {
    justify-content: flex-start;
  }
  .status-line-right {
    justify-content: flex-end;
    /* Relaxed breathing room to the right of the version / online chip so it
       doesn't sit flush against the edge (the base inset is only 2px). */
    padding-right: 0.75em;
  }
  .status-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.4em;
    flex-shrink: 0;
  }
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #ef4444;
  }
  .status-chip[data-online='true'] .dot {
    background: #22c55e;
  }
  /* A new version installing: amber, pulsing until the worker takes over. */
  .status-chip[data-updating='true'] .dot {
    background: #f59e0b;
    animation: status-updating var(--dur-pulse) ease-in-out infinite;
  }
  @keyframes status-updating {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.35; }
  }
  /* The bar's three texts — next event, the size readout, the ONLINE / version
     chip — share one face, size and line box, so each centres on the same line. */
  .next-event,
  .size-readout,
  .status-chip {
    font-size: var(--fs-11);
    line-height: 1;
    letter-spacing: 0.04em;
  }
  .next-event {
    overflow: hidden;
    flex: 1 1 auto;
    min-width: 0;
  }
  /* Static (fits, or reduced motion): clip with an ellipsis as before. */
  .next-event-static {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  /* Marquee: two copies + a gap scroll as one seamless loop. The initial 2s
     delay holds the start still before the first scroll; --marquee-shift (one
     copy + the gap) lands the second copy exactly where the first began. */
  .next-event-track {
    display: inline-flex;
    gap: 48px; /* = MARQUEE_GAP_PX; also the seamless-loop shift offset */
    white-space: nowrap;
    will-change: transform;
  }
  .next-event-track.marquee {
    animation: next-marquee var(--marquee-dur) linear 2s infinite;
  }
  .next-event-copy {
    white-space: nowrap;
  }
  @keyframes next-marquee {
    from { transform: translateX(0); }
    to { transform: translateX(calc(-1 * var(--marquee-shift))); }
  }
  .toggle {
    display: inline-flex;
    align-items: center;
    justify-self: center;
    color: var(--ink-color);
    /* Rotated via inline style to point up/down/left/right; animate the turn
       (neutralized under reduced motion globally). */
    transition: transform var(--dur-ui) ease;
  }
  .selection-head {
    display: flex;
    align-items: center;
    gap: var(--toolbar-gap);
    /* Height is driven by its 28px buttons + the shared spacing inset, so its
       gap/margin match the top toolbar. */
    height: auto;
    padding: var(--time-header-pad-x);
    cursor: pointer;
    touch-action: none;
  }
  /* The undo bar's note takes the room; UNDO and dismiss sit at the right. */
  .undo-message {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: var(--fs-12);
  }
  /* DELETE and MOVE sit at the start; CANCEL is pushed to the far right. */
  .sel-cancel-wrap {
    margin-left: auto;
    display: inline-flex;
  }
  .sel-count {
    font-size: var(--fs-12);
    letter-spacing: 0.04em;
    white-space: nowrap;
  }
  .sel-btn {
    height: 28px;
    padding: 0 0.95em;
    border: var(--btn-border-w) solid var(--ink-color);
    background: var(--paper-color);
    color: var(--ink-color);
    cursor: pointer;
    font-size: var(--fs-12);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    white-space: nowrap;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  /* MOVE/COPY: label + nbsp + icon, centered, with the icon slot reserved in
     every state so the button width stays constant (mirrors ConfirmButton). */
  .sel-stack {
    display: inline-grid;
  }
  .sel-stack > * {
    grid-area: 1 / 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .sel-sizer {
    visibility: hidden;
  }
  .sel-icon-box {
    display: inline-block;
    width: 13px;
    height: 13px;
  }
  /* The undo countdown takes the icon slot; its number ticks once a second so
     the closing window is visible without a (motion-gated) animation. */
  .sel-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 13px;
    height: 13px;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }
  .move-menu {
    position: relative;
    display: inline-flex;
  }
  .move-menu-list {
    position: absolute;
    bottom: calc(100% + 4px);
    right: 0;
    z-index: 10;
    display: flex;
    flex-direction: column;
    min-width: 9em;
    max-height: 50vh;
    overflow: auto;
    border: var(--btn-border-w) solid var(--ink-color);
    background: var(--paper-color);
  }
  .move-menu-item {
    text-align: left;
    padding: 0.4em 0.6em;
    border: none;
    background: var(--paper-color);
    color: var(--ink-color);
    cursor: pointer;
    font-size: var(--fs-12);
    white-space: nowrap;
  }
  .move-menu-item + .move-menu-item {
    border-top: var(--border-w) dashed var(--ink-color);
  }
  /* Hover cue is the accent text tint from the global button:hover rule — no fill. */

  /* Tray */
  .events-tray {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  /* Desktop left mode: the tray detaches from the bottom bar and becomes a
     fixed panel pinned to the left edge (below the toolbar, above the still-
     visible bottom bar). It slides in via translateX; the timeline is pushed
     right by --tray-left-w so nothing is obscured. top/bottom come from an
     inline style (they depend on the search bar and the measured bar height).
     Reduced motion neutralizes the transition globally (see global.css). */
  .events-tray.side-left {
    position: fixed;
    left: 0;
    width: var(--tray-side-w);
    transform: translateX(-100%);
    transition: transform var(--dur-ui) ease;
    background: var(--paper-color);
    border-right: calc(var(--border-w) + 1px) solid var(--ink-color);
    box-shadow: 2px 0 6px rgba(0, 0, 0, 0.12);
    z-index: var(--z-tray);
  }
  .events-tray.side-left[data-open='true'] {
    transform: translateX(0);
  }
  /* In left mode the tray's control bar acts as the panel's own toolbar: move it
     to the top and size it to the main toolbar (--toolbar-h) with a solid bottom
     border, so it lines up with the app toolbar to its right and the event list
     below starts level with the timeline rows. Ordering via `order` keeps the
     DOM structure (and bottom-mode layout) untouched. */
  .events-tray.side-left .copy-bar {
    order: -2;
    height: var(--toolbar-h);
    border-top: 0;
    border-bottom: var(--border-w) solid var(--ink-color);
  }
  .events-tray.side-left .filter-panel {
    order: -1;
  }
  .filter-panel {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 0.3em;
    padding: 0.4em 0;
    border-top: var(--border-w) dashed var(--ink-color);
    background: var(--paper-2);
    user-select: none;
    -webkit-user-select: none;
  }
  .filter-row {
    display: flex;
    align-items: center;
    gap: 0.25em;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: visible;
    scrollbar-width: none;
    max-width: 100%;
  }
  .filter-row::-webkit-scrollbar { display: none; }
  .filter-clear {
    font-size: var(--fs-12);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 0 calc(0.5em - 2px);
    margin-left: 0.6em;
    border: var(--border-w) solid var(--ink-faint);
    border-radius: 0;
    background: var(--paper-color);
    color: var(--ink-muted);
    cursor: pointer;
    flex-shrink: 0;
    white-space: nowrap;
  }
  .filter-clear[data-active='true'] {
    border-color: var(--ink-color);
    color: var(--ink-color);
  }
  .filter-chip {
    font-size: var(--fs-12);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 0 calc(0.5em - 2px);
    border: var(--border-w) dashed var(--ink-color);
    border-radius: 0;
    background: transparent;
    color: var(--ink-color);
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .filter-chip[aria-pressed='true'] {
    border-style: solid;
    border-color: var(--ink-color);
    background: var(--ink-color);
    color: var(--paper-color);
  }
  .copy-bar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: var(--toolbar-gap);
    padding: var(--time-header-pad-x);
    border-top: var(--border-w) dashed var(--ink-color);
  }
  .copy-spacer {
    flex: 1 1 auto;
  }
  .copy-btn {
    height: 28px;
    padding: 0 12px;
    border: var(--btn-border-w) solid var(--ink-color);
    background: var(--paper-color);
    color: var(--ink-color);
    cursor: pointer;
    font-size: var(--fs-12);
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  /* Persistent pressed state keeps the inverted fill; hover is just the accent tint. */
  .copy-btn[aria-pressed='true'] {
    background: var(--ink-color);
    color: var(--paper-color);
  }
  .copy-btn[data-filter-active='true'] {
    border-style: dashed;
  }
  .copy-btn[data-toggle='true'] {
    padding: 0;
    width: 28px;
    min-width: 28px;
  }
  .event-counter {
    font-size: var(--fs-12);
    color: var(--ink-color);
    padding: 0 0.5em;
    white-space: nowrap;
  }
  .raw-block {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    padding: 0.6em 0.8em;
    user-select: text;
    -webkit-user-select: text;
  }
  .raw-table {
    border-collapse: collapse;
    font-family: var(--mono);
    font-size: var(--fs-11);
    line-height: 1.4;
    white-space: nowrap;
  }
  .raw-table th,
  .raw-table td {
    padding: 0.15em 0.6em 0.15em 0;
    text-align: left;
    vertical-align: top;
  }
  /* A capped column clips with an ellipsis (display only: the text is all
     there for copying). */
  .raw-cell {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .raw-table th {
    font-weight: 600;
    border-bottom: var(--border-w) solid var(--ink-faint);
  }
  .tray-scroll {
    flex: 1 1 auto;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 0.4em 0.6em 0.5em;
    user-select: text;
    -webkit-user-select: text;
  }
  .week-group {
    margin-bottom: 0.8em;
  }
  h2.week-label {
    margin: 0 0 0.3em;
    padding-bottom: 0.2em;
    /* The same muted ink as the category subheadings under it. */
    border-bottom: var(--border-w) solid var(--ink-muted);
    font-size: var(--fs-12);
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }
  /* The temp marker's span, heading the week groups it covers — accent, like
     every other readout that names the marker. */
  h2.span-title {
    margin: 0 0 0.5em;
    font-size: var(--fs-12);
    font-weight: 700;
    letter-spacing: 0.05em;
    color: var(--accent-color);
  }
  .cat-group {
    margin-bottom: 0.4em;
  }
  h3.cat-label {
    display: block;
    font-size: var(--fs-12);
    font-weight: normal;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ink-muted);
    margin: 0.6em 0 0.15em;
  }
  .event-list {
    display: flex;
    flex-direction: column;
  }
  .event-row {
    display: grid;
    /* Give the title most of the row; cap the location so it can't crowd it out. */
    grid-template-columns: auto minmax(0, 1fr) minmax(0, 6em);
    gap: 0.5em;
    align-items: baseline;
    width: 100%;
    font-size: var(--fs-12);
    padding: 1px 0;
    border: 0;
    background: transparent;
    color: inherit;
    text-align: left;
    cursor: pointer;
  }
  .event-row:focus,
  .event-row:focus-visible {
    outline: none;
    text-decoration: underline;
  }
  .event-time {
    font-family: var(--mono);
    color: var(--ink-muted);
    font-size: var(--fs-12);
    white-space: nowrap;
  }
  .event-title {
    font-family: var(--title-font);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }
  .event-loc {
    font-size: var(--fs-12);
    color: var(--ink-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    text-align: right;
  }
  .empty {
    margin: 0;
    color: var(--ink-muted);
    font-size: var(--fs-12);
  }
</style>
